/**
 * Simple Token Expiration Popup
 * Shows popup 1 minute before token expires
 */

import { Button } from '@heltarchat/ui';
import React, { useEffect, useState } from 'react';

import { ModalWithCard } from 'app/components/AtomComponents/ModalWithCard';
import { TopShadcnHeader } from 'app/components/AtomComponents/TopGreenHeader';
import { P } from 'app/components/AtomComponents/P';
import {
  getTimeUntilExpiry,
  startTokenMonitoring,
  stopTokenMonitoring,
  handleExpiredToken,
  isForcedExpired,
} from 'utils/tokenExpirationHelper';

export const TokenExpirationPopup: React.FC = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    // Start monitoring when component mounts
    startTokenMonitoring(() => {
      setShowWarning(true);
      const remaining = getTimeUntilExpiry();
      setTimeRemaining(remaining);
      setIsExpired(remaining <= 0 || isForcedExpired());
    });

    // Cleanup when component unmounts
    return () => {
      stopTokenMonitoring();
    };
  }, []);

  useEffect(() => {
    // Update countdown every second when warning is shown
    if (!showWarning) return;

    const interval = setInterval(() => {
      const remaining = getTimeUntilExpiry();
      setTimeRemaining(remaining);

      // Check if token just expired (or the session was revoked server-side)
      if ((remaining <= 0 || isForcedExpired()) && !isExpired) {
        setIsExpired(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showWarning, isExpired]);

  const handleSignInNow = () => {
    setShowWarning(false);
    handleExpiredToken();
  };

  const handleContinueSession = () => {
    setShowWarning(false);
  };

  const formatTime = (ms: number): string => {
    const seconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  if (!showWarning) {
    return null;
  }

  return (
    <ModalWithCard classNameCard='w-[95%] max-w-[400px] p-0'>
      <TopShadcnHeader
        headerTitle={isExpired ? 'Session Expired' : 'Session Expiring'}
        onClose={() => {}}
      />

      <div className='p-6 space-y-4'>
        <div className='text-center space-y-3'>
          <div className='text-4xl mb-2'>{isExpired ? '🔒' : '⚠️'}</div>

          {isExpired ? (
            <>
              <P
                fontsize='16'
                fontweight='600'
                textalign='center'
                marginbottom='8'
              >
                Your session has expired
              </P>

              <P
                fontsize='12'
                color='#666'
                textalign='center'
                marginbottom='16'
              >
                Please sign in again to continue.
              </P>
            </>
          ) : (
            <>
              <P
                fontsize='16'
                fontweight='600'
                textalign='center'
                marginbottom='8'
              >
                Your session will expire soon
              </P>

              <P fontsize='14' textalign='center' marginbottom='8'>
                {`Time remaining: ${formatTime(timeRemaining)}`}
              </P>

              <P
                fontsize='12'
                color='#666'
                textalign='center'
                marginbottom='16'
              >
                Please sign in again to continue working.
              </P>
            </>
          )}
        </div>

        <div className='flex flex-col gap-3'>
          <Button onClick={handleSignInNow} className='w-full !btn-heltar'>
            Sign In Again
          </Button>

          {!isExpired && (
            <Button
              onClick={handleContinueSession}
              variant='outline'
              className='w-full'
            >
              Continue this Session
            </Button>
          )}
        </div>
      </div>
    </ModalWithCard>
  );
};

export default TokenExpirationPopup;
